import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Session,
  ValidateSessionSuccessPayload,
} from "../../types/session.definition";
import { RootState } from "../store";

interface SessionState {
  session: Session | null;
  expiresAt: string | null;
  loading: boolean;
  error: string | null;
  sessionId: string | null;
  sessionStatus: "INVALID_SESSION" | "VALID_SESSION" | null;
  features: Record<string, boolean>;
  /**
   * MED-1621 PIN lock. `null` = unknown: the API predates the PIN lock (no `hasPin` in the
   * validate-session answer), the lock screen is then off.
   */
  hasPin: boolean | null;
  /** The session must be unlocked with the PIN before any other call. */
  locked: boolean;
  /**
   * MED-1621 security answers that end the session: a short message is shown, then the session
   * saga sends the user to the login page.
   */
  forcedLogout: ForcedLogoutReason | null;
}

export type ForcedLogoutReason = "REAUTH_REQUIRED" | "SESSION_REUSE";

const initialState: SessionState = {
  session: null,
  expiresAt: null,
  loading: true,
  error: null,
  sessionId: null,
  sessionStatus: null,
  features: {},
  hasPin: null,
  locked: false,
  forcedLogout: null,
};

const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    validateSessionRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    validateSessionSuccess: (
      state,
      action: PayloadAction<ValidateSessionSuccessPayload>
    ) => {
      state.error = null;
      state.sessionId = action.payload.sessionId;
      state.expiresAt = action.payload.expiresAt;
      state.sessionStatus = action.payload.sessionStatus;
      state.features = action.payload.settings?.features ?? {};
      const hasPin = action.payload.hasPin;
      state.hasPin = typeof hasPin === "boolean" ? hasPin : null;
      state.locked = state.hasPin === true && action.payload.locked === true;
    },
    validateSessionFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.sessionId = null;
      state.expiresAt = null;
      state.sessionStatus = "INVALID_SESSION";
    },
    getSessionRequest: (state) => {
      state.error = null;
    },
    getSessionSuccess: (state, action: PayloadAction<Session>) => {
      state.loading = false;
      state.session = action.payload;
      state.error = null;
    },
    getSessionFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.sessionStatus = "INVALID_SESSION";
    },
    clearSession: (state) => {
      state.session = null;
      state.loading = false;
      state.error = null;
    },
    requestTerminateSessionRequest: (state) => {
      state.loading = true;
    },
    setPinStatus: (
      state,
      action: PayloadAction<{ hasPin: boolean; locked: boolean }>
    ) => {
      state.hasPin = action.payload.hasPin;
      state.locked = action.payload.hasPin && action.payload.locked;
    },
    /** MED-1643: lock delay saved in the settings — the lock timer uses it at once. */
    setPinLockMinutes: (state, action: PayloadAction<number>) => {
      if (state.session) {
        state.session.pinLockMinutes = action.payload;
      }
    },
    /** 423 SESSION_LOCKED, inactivity or another tab: only a session with a PIN can be locked. */
    lockSession: (state) => {
      if (state.hasPin === true) {
        state.locked = true;
      }
    },
    /** 401 PIN_ATTEMPTS_EXCEEDED: every token is revoked — handled by the session saga. */
    pinAttemptsExceeded: () => {},
    /** 403 PIN_CREATE_REAUTH_REQUIRED or 401 SESSION_REUSE_DETECTED — handled by the session saga. */
    forceLogin: (state, action: PayloadAction<ForcedLogoutReason>) => {
      state.forcedLogout = action.payload;
    },
    expireSession: (state) => {
      state.sessionStatus = "INVALID_SESSION";
      state.session = null;
      state.sessionId = null;
      state.expiresAt = null;
      state.loading = false;
      state.error = "Session expired";
    },
  },
});

export const {
  validateSessionRequest,
  validateSessionSuccess,
  validateSessionFailure,
  getSessionRequest,
  getSessionSuccess,
  getSessionFailure,
  clearSession,
  requestTerminateSessionRequest,
  expireSession,
  setPinStatus,
  setPinLockMinutes,
  lockSession,
  pinAttemptsExceeded,
  forceLogin,
} = sessionSlice.actions;

export default sessionSlice.reducer;
export const selectSessionStaticData = (state: RootState) => {
  return {
    sessionStatus: state.session.sessionStatus,
    expiresAt: state.session.expiresAt,
    sessionId: state.session.sessionId,
  };
};

export const selectSessionAuthState = (state: RootState) => {
  return {
    sessionStatus: state.session.sessionStatus,
    loading: state.session.loading,
    session: state.session.session,
    expiresAt: state.session.expiresAt,
  };
};

export const selectFeatureFlags = (state: RootState) => {
  return state.session.features;
};

export const selectFeatureFlag = (state: RootState, flagName: string) => {
  return state.session.features[flagName] ?? false;
};

export const selectPinLockState = createSelector(
  [
    (state: RootState) => state.session.sessionStatus,
    (state: RootState) => state.session.hasPin,
    (state: RootState) => state.session.locked,
  ],
  (sessionStatus, hasPin, locked) => ({ sessionStatus, hasPin, locked })
);

export const selectForcedLogout = (state: RootState) => state.session.forcedLogout;
